/**
 * `<gh-icon name="target">` - one drawing from `frontend/icons.ts`, inlined.
 *
 * The drawings and the reasoning behind the set live in `icons.ts`; this file is only the element
 * that puts one on screen. What it owns is the three decisions every call site would otherwise have
 * to remember, and get wrong differently:
 *
 * **Size follows the TEXT.** The default box is `1em`, because almost every icon here replaced an
 * emoji sitting inside a sentence, a heading or a button label - and an emoji scaled with its text.
 * A fixed `16px` default would have quietly shrunk every icon in an `<h2>` and grown the ones in a
 * `.hint`. `size` overrides it in pixels for the few places an icon is a standalone graphic rather
 * than a character-shaped thing. The `-0.135em` baseline shift is what an inline-block box needs to
 * sit where the glyph it replaced sat; without it every converted line moves by a couple of pixels.
 *
 * **Color follows the TEXT too** (`currentColor`), which is the whole reason an SVG beats an emoji
 * here: a warning chip's icon is the chip's color, a disabled button's icon dims with its label, and
 * light mode needs no icon work at all. ⚠ This is what makes B.6 a precondition for B.5 rather than
 * a sibling of it - an emoji cannot take a color, so no amount of palette work could have made the
 * old icons theme.
 *
 * **Silence is the default, and it is a decision rather than an omission.** An emoji carries an
 * implicit accessible name that a screen reader announces; a bare `<svg>` carries none. So a
 * mechanical conversion would have made this app quieter for a screen-reader user than the emoji
 * were - and the fix is NOT to label everything, because the common case is an icon sitting beside
 * the very words it depicts, where a label makes the reader hear "megaphone Boost post" instead of
 * "Boost post". Decorative is therefore the default (`aria-hidden`), and `label` is the explicit
 * opt-in for the case where the icon IS the only text (an icon-only button, a bare status pill).
 * ⚠ If you are converting a glyph that stood alone with no words beside it, it needs a `label`.
 *
 * `unsafeSVG` is safe here in the one way that word can be: its argument is never data, only a
 * compile-time constant looked up from this app's own module by a typed key. An unknown name renders
 * NOTHING rather than throwing - a missing icon should not take a page down - but it also warns once
 * per name, because a silent blank is exactly the failure mode the sprite experiment showed is
 * impossible to notice.
 */
import { css, html, nothing, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeSVG } from "lit/directives/unsafe-svg.js";
import { GhElement } from "./ghElement.ts";
import { ICONS, isIconName, type IconName } from "../icons.ts";
import { focusStyles } from "./sharedStyles.ts";

const warned = new Set<string>();

@customElement("gh-icon")
export class GhIcon extends GhElement {
	/**
	 * Which drawing.
	 *
	 * ⚠ Typed `string`, not `IconName`, and that is deliberate: this value can arrive from an
	 * ATTRIBUTE, so at runtime it is whatever the DOM says. Declaring it `IconName` narrows the
	 * unknown-name branch below to `never` and the compiler then proves a guard unreachable that a
	 * typo in a template reaches on the first render. `icon()` is the typed door - callers go through
	 * it and get their name checked; the element itself validates what it was actually handed.
	 */
	@property({ type: String }) accessor name = "";

	/** Pixel override for the box. Unset = `1em`, i.e. the size of the text around it. */
	@property({ type: Number }) accessor size: number | null = null;

	/**
	 * The icon's accessible name. Set it ONLY when the icon carries meaning no neighbouring text
	 * already carries; leaving it unset marks the icon decorative, which is the common case.
	 */
	@property({ type: String }) accessor label = "";

	static styles = [
		focusStyles,
		css`
			:host {
				display: inline-block;
				width: 1em;
				height: 1em;
				vertical-align: -0.135em;
				flex: none;
				line-height: 0;
			}
			svg {
				display: block;
				width: 100%;
				height: 100%;
			}
		`
	];

	render(): TemplateResult | typeof nothing {
		const name = this.name;
		if (name === "" || !isIconName(name)) {
			if (name !== "" && !warned.has(name)) {
				warned.add(name);
				// i18n-ignore: a developer diagnostic in the browser console, which has no locale
				console.warn(`<gh-icon>: no icon named "${name}"`);
			}
			return nothing;
		}
		const icon = ICONS[name];
		const decorative = this.label === "";
		// A `size` is a box override, so it goes on the HOST (which carries the 1em default), not on
		// the <svg>, which is 100% of whatever the host ended up being.
		if (this.size != null) {
			this.style.width = `${this.size}px`;
			this.style.height = `${this.size}px`;
		}
		return html`<svg
			viewBox="0 0 24 24"
			fill=${icon.filled === true ? "currentColor" : "none"}
			stroke=${icon.filled === true ? "none" : "currentColor"}
			stroke-width="1.75"
			stroke-linecap="round"
			stroke-linejoin="round"
			role=${decorative ? "presentation" : "img"}
			aria-hidden=${decorative ? "true" : nothing}
			aria-label=${decorative ? nothing : this.label}
		>
			${unsafeSVG(icon.body)}
		</svg>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"gh-icon": GhIcon;
	}
}

/**
 * `icon("target")` - the element as a value, for the two places a tag literal will not do.
 *
 * The first is an icon INSIDE a translated sentence, and it is the reason this round is an i18n
 * migration rather than a UI one. ~99 dictionary entries had a glyph in the middle of the prose
 * ("Run 🎯 Detect sections in 🎛️ Sections & Lyrics first"), pointing the reader at controls by their
 * picture - so the picture cannot be moved to the call site the way a leading one can, and it cannot
 * be left behind either, because the control it points at now draws an SVG and the sentence would be
 * naming a picture that no longer exists anywhere.
 *
 * The seam for it already existed and needed nothing new: `withNodes()` (round 80) splices values
 * into a translated sentence at its `{name}` placeholders, and `t()` leaves a placeholder it got no
 * param for intact. So `"Run {detect} Detect sections"` carries the icon as a NAMED PARAMETER, which
 * is the same rule the app already applies to every other value in a sentence - a translator can move
 * it, and no word order is baked in. ⚠ **`{icon}` is not a magic token**: it is an ordinary parameter
 * whose value happens to be an element, so a sentence with two of them names them for what they point
 * at (`{detect}`, `{sections}`), never `{icon1}`/`{icon2}`.
 *
 * The second is a conditional - `${ready ? icon("check-circle") : icon("hourglass")}` - where a tag
 * literal would mean repeating the whole element twice.
 *
 * ⚠ Neither case exists in an ATTRIBUTE. A `title=`/`aria-label=` value is plain text by
 * construction, so an icon cannot go in one at all: those sentences either name the control in words
 * or say nothing. That is a real narrowing of what the emoji could do, and it is the correct one - a
 * tooltip that says "press 🎬" was already unreadable to a screen reader.
 */
export function icon(name: IconName, opts?: { size?: number; label?: string }): TemplateResult {
	return html`<gh-icon name=${name} size=${opts?.size ?? nothing} label=${opts?.label ?? nothing}></gh-icon>`;
}
